import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center mb-10">
            <img
              src="/xtrack-logo.png"
              alt="Xtrack"
              className="h-12 w-auto max-w-[220px] object-contain"
              data-testid="login-logo"
            />
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-3">
            Sign in
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight mb-2">
            Welcome back.
          </h1>
          <p className="text-sm text-neutral-500 mb-8">
            Run your agency like a control room.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-semibold text-neutral-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest font-semibold text-neutral-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1 rounded-sm"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full rounded-sm bg-neutral-900 hover:bg-neutral-800 text-white"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-xs text-neutral-500">
            Need an account?{" "}
            <span className="text-neutral-900 font-semibold">
              Ask your studio admin to invite you.
            </span>
          </p>

          <div className="mt-10 p-4 bg-neutral-50 border border-neutral-200 rounded-sm text-xs text-neutral-600 leading-relaxed" data-testid="login-info-panel">
            <div className="font-semibold text-neutral-900 mb-2">Looking for something?</div>
            <p className="mb-2">
              Want a demo of the productivity tool? Write to{" "}
              <a href="mailto:anilbhat@xebecindia.com" className="font-semibold text-neutral-900 underline" data-testid="demo-contact-email">
                anilbhat@xebecindia.com
              </a>
              .
            </p>
            <p>
              Trying to reach Xebec Communications? Visit{" "}
              <a href="https://www.xebecindia.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-neutral-900 underline" data-testid="xebec-site-link">
                www.xebecindia.com
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      {/* Right: poster */}
      <div className="hidden lg:flex flex-1 bg-neutral-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">
            Studio/OS — vol. 01
          </div>
          <div>
            <div className="font-display text-6xl font-bold tracking-tight leading-[0.95]">
              Projects.<br/>Clients.<br/>Hours.<br/>Margins.
            </div>
            <div className="mt-6 text-sm text-neutral-400 max-w-md">
              One operating system for the modern digital agency.
              Plan work, track time, bill clients, and let AI write the boring reports.
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 text-xs border-t border-neutral-800 pt-6">
            <div>
              <div className="text-2xl font-display font-bold">12k+</div>
              <div className="text-neutral-500 uppercase tracking-widest text-[10px]">Hours tracked</div>
            </div>
            <div>
              <div className="text-2xl font-display font-bold">340</div>
              <div className="text-neutral-500 uppercase tracking-widest text-[10px]">Projects shipped</div>
            </div>
            <div>
              <div className="text-2xl font-display font-bold">$4.1M</div>
              <div className="text-neutral-500 uppercase tracking-widest text-[10px]">Billed via Studio</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
